import { InvestorDetailsHouseholdNotesQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import Button from '@/components/basicComponents/button';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import React, { useState } from 'react';
import styled from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../types';

interface MobileNotesProps {
  entityType: InvestorDetailsEntityTypeEnum;
  legalEntityNotes: InvestorDetailsHouseholdNotesQuery['getInvestor']['legalEntityNotes'];
  aboutInvestorNotes: { id: string; entityName: string; note: string }[];
  handleEntitySelect: (entityId: string) => void;
  handleModalWindow: ({ type, note }: { type: string; note?: { legalEntityId: string; date: string; note: string } | null }) => void;
}

const MobileNotes = ({ entityType, legalEntityNotes, aboutInvestorNotes, handleEntitySelect, handleModalWindow }: MobileNotesProps) => {
  const [showAboutInvestorNotes, setShowAboutInvestorNotes] = useState(true);

  return (
    <MoreDataWrap id="modal">
      <NotesBody id="notes">
        {aboutInvestorNotes?.length > 0 && (
          <>
            <NotesHeader cursor="pointer" onClick={() => setShowAboutInvestorNotes(!showAboutInvestorNotes)}>
              About Investor
              {showAboutInvestorNotes ? (
                <ArrowUp width={23} height={23} fill={themeVar('font.action')} />
              ) : (
                <ArrowDown style={{ position: 'relative', top: '1px', right: '4px' }} width={12} height={12} fill={themeVar('font.action')} />
              )}
            </NotesHeader>
            <NotesWrapper showAboutInvestorNotes={showAboutInvestorNotes}>
              <ExpandableWrapp>
                {aboutInvestorNotes.map((aboutInvestorNoteItem) => (
                  <React.Fragment key={aboutInvestorNoteItem.id}>
                    <NoteItem id="notes">
                      <NoteItemTitleWrap>
                        <NoteItemTitle
                          id="notes"
                          withRedirect={entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group}
                          onClick={() =>
                            entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group
                              ? handleEntitySelect(aboutInvestorNoteItem.id)
                              : null
                          }
                        >
                          {aboutInvestorNoteItem.entityName}
                        </NoteItemTitle>
                      </NoteItemTitleWrap>
                      <NoteItemDesc id="notes">{aboutInvestorNoteItem.note}</NoteItemDesc>
                    </NoteItem>
                  </React.Fragment>
                ))}
              </ExpandableWrapp>
            </NotesWrapper>
          </>
        )}
        <NotesHeader>Notes</NotesHeader>
        {legalEntityNotes.map(({ legalEntityId, name, notes }) => {
          return notes.map(({ date, note }) => (
            <React.Fragment key={guid()}>
              <NoteItem id="notes">
                <NoteItemTitleWrap>
                  <NoteItemTitle
                    id="notes"
                    withRedirect={entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group}
                    onClick={() =>
                      entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group
                        ? handleEntitySelect(legalEntityId)
                        : null
                    }
                  >
                    {name}
                  </NoteItemTitle>
                  <ActionIconsWrap>
                    <EditIcon
                      fill={themeVar('action.primary')}
                      onClick={() => handleModalWindow({ type: 'edit-note', note: { legalEntityId, date, note } })}
                    />
                    <DeleteItemIcon
                      fill={themeVar('action.primary')}
                      width={28}
                      height={28}
                      onClick={() => handleModalWindow({ type: 'delete-note', note: { legalEntityId, date, note } })}
                    />
                  </ActionIconsWrap>
                </NoteItemTitleWrap>
                <NoteItemDate id="notes">{date ? format(new Date(date), 'LLL d, yyyy') : 'No date'}</NoteItemDate>
                <NoteItemDesc id="notes">{note}</NoteItemDesc>
              </NoteItem>
              <Divider />
            </React.Fragment>
          ));
        })}
        <AddButtonWrapper>
          <Button size="md" onClick={() => handleModalWindow({ type: 'edit-note' })} icon={<AddButtonIcon />}>
            Add New Note
          </Button>
        </AddButtonWrapper>
      </NotesBody>
    </MoreDataWrap>
  );
};

export default MobileNotes;

const MoreDataWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Divider = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${themeVar('border.base')};
  margin: 5px 0;
`;

const AddButtonWrapper = styled.div`
  margin-top: 10px;
`;

const NotesHeader = styled.div<{ cursor?: string }>`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${themeVar('font.weak')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ cursor }) => cursor || 'default'};
  border-bottom: 1px solid ${themeVar('border.base')};
  padding: 10px 0;
  margin-bottom: 5px;
`;

const NotesWrapper = styled.div<{ showAboutInvestorNotes: boolean }>`
  display: grid;
  grid-template-rows: ${({ showAboutInvestorNotes }) => (showAboutInvestorNotes ? '1fr' : '0fr')};
  overflow: ${({ showAboutInvestorNotes }) => (showAboutInvestorNotes ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const ExpandableWrapp = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const NoteItemDesc = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${themeVar('font.base')};
  margin-top: 10px;
`;

const NoteItemDate = styled.div`
  font-family: Blinker, serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: ${themeVar('font.weak')};
`;

const NoteItemTitle = styled.div<{ withRedirect?: boolean }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  color: ${({ withRedirect }) => (withRedirect ? themeVar('font.action') : themeVar('font.base'))};
`;

const NoteItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: 0.3s;
  padding: 30px 20px;
  border-radius: 8px;

  &:hover {
    background: ${themeVar('layer.2')};
  }
`;

const NotesBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  overscroll-behavior: none;
`;

const NoteItemTitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionIconsWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;
